import { useMemo, useState } from 'react';
import { useAdminI18n } from '../../../lib/i18n/use-admin-i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import { useAuthStore } from '../../../stores/authStore';
import { DynamicTable } from '../../../components/custom/table/DynamicTable';
import type {
  DynamicTableColumnConfig,
  DynamicTableControllers,
} from '../../../components/custom/table/dynamic-table-types';
import type { PlanDto } from '@cms/models';
import { SearchFilter, StatusFilter } from '../../../components/custom/filters';
import { STATUS_OPTIONS } from '@cms/utils';
import { saasApi } from '../../../lib/api/saas.api';
import {
  AdminTableRowActions,
  PagePrimaryActionButton,
} from '../../../components/common/AdminTableRowActions';
import { NativeButtonWithIcon } from '../../../components/common/NativeButtonWithIcon';
import { PlanFormModal, type PlanFormModalMode } from './PlanFormModal';

type PlanStatusFilter = (typeof STATUS_OPTIONS)[number]['value'] | '';

interface PlansTableProps {
  rows: PlanDto[];
  controllers?: DynamicTableControllers;
  rowOffset: number;
  onView: (plan: PlanDto) => void;
  onEdit: (plan: PlanDto) => void;
  onDelete: (plan: PlanDto) => void;
  isDeleting: boolean;
  isLoading: boolean;
}

function PlansTable(props: Readonly<PlansTableProps>) {
  const { rows, controllers, rowOffset, onView, onEdit, onDelete, isDeleting, isLoading } = props;
  const { col, act, page, statusOptions, tableLoading } = useAdminI18n();

  const columns: DynamicTableColumnConfig<PlanDto>[] = useMemo(
    () => [
    {
      id: 'no',
      header: col('no'),
      columnType: 'number',
      cell: ({ index }) => rowOffset + index + 1,
      widthMode: 'fit',
      size: 64,
      meta: { className: 'w-16 text-muted-foreground' },
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: col('name'),
      sortable: true,
      columnType: 'text',
    },
    {
      id: 'code',
      accessorKey: 'code',
      header: col('code'),
      sortable: true,
      columnType: 'text',
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: col('description'),
      columnType: 'text',
    },
    {
      id: 'billingPeriod',
      accessorKey: 'billingPeriod',
      header: col('billing'),
      sortable: true,
      columnType: 'text',
      widthMode: 'fit',
      size: 100,
    },
    {
      id: 'maxUsers',
      accessorKey: 'maxUsers',
      header: col('max_users'),
      columnType: 'number',
      widthMode: 'fit',
      size: 100,
      cell: ({ value }) => (value != null ? String(value) : '—'),
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: col('status'),
      sortable: true,
      columnType: 'badge',
      displayLabelOptions: statusOptions,
      widthMode: 'fit',
      size: 100,
      meta: { className: 'min-w-[100px]' },
    },
    {
      id: 'actions',
      header: col('actions'),
      columnType: 'actions',
      cell: ({ row }) => (
        <AdminTableRowActions
          act={act}
          onView={() => onView(row)}
          onEdit={() => onEdit(row)}
          onDelete={() => onDelete(row)}
          isDeleting={isDeleting}
        />
      ),
      widthMode: 'fit',
      size: 220,
      minSize: 180,
      meta: { className: 'min-w-[180px] text-right' },
    },
    ],
    [act, col, isDeleting, onDelete, onEdit, onView, rowOffset, statusOptions],
  );

  return (
    <DynamicTable<PlanDto>
      features={{
        columns: true,
        rows: true,
        sorting: true,
        pagination: true,
        globalFilter: false,
        stripedRows: true,
        fontSize: 'xs',
        compactPadding: true,
      }}
      columns={columns}
      rows={{
        data: rows,
        getRowId: row => row.id as string,
      }}
      controllers={controllers}
      isLoading={isLoading}
      loadingMessage={tableLoading}
      emptyMessage={page('plans', 'empty')}
    />
  );
}

export function PlansPage() {
  const { col, page, statusOptions, allStatusesOption } = useAdminI18n();
  const tenantId = useAuthStore(s => s.tenantId ?? null);
  const queryClient = useQueryClient();
  const [sorting, setSorting] = useState<SortingState>([{ id: 'code', desc: false }]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PlanStatusFilter>('');
  const [formModal, setFormModal] = useState<{
    open: boolean;
    mode: PlanFormModalMode;
    plan: PlanDto | null;
  }>({ open: false, mode: 'create', plan: null });
  const [assignPlanId, setAssignPlanId] = useState('');

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['saas-plans'],
    queryFn: () => saasApi.getPlans(),
  });

  const assignMutation = useMutation({
    mutationFn: (planId: string) =>
      saasApi.assignPlanToTenant(tenantId!, { planId, startsAt: new Date().toISOString() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saas-current-plan'] });
      setAssignPlanId('');
    },
  });

  const filteredPlans = useMemo(() => {
    let list = plans;
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(p => {
        const code = (p.code ?? '').toLowerCase();
        const name = (p.name ?? '').toLowerCase();
        const desc = (p.description ?? '').toLowerCase();
        return code.includes(term) || name.includes(term) || desc.includes(term);
      });
    }
    if (statusFilter) {
      list = list.filter(p => (p.status ?? '') === statusFilter);
    }
    return list;
  }, [plans, search, statusFilter]);

  const sortedPlans = useMemo(() => {
    if (!sorting.length) return filteredPlans;
    const first = sorting[0];
    if (!first) return filteredPlans;
    const { id, desc } = first;
    const field = id as keyof PlanDto;
    return [...filteredPlans].sort((a, b) => {
      const av = (a as Record<string, unknown>)[field];
      const bv = (b as Record<string, unknown>)[field];
      if (av == null && bv == null) return 0;
      if (av == null) return desc ? -1 : 1;
      if (bv == null) return desc ? 1 : -1;
      const as = typeof av === 'object' ? JSON.stringify(av) : String(av);
      const bs = typeof bv === 'object' ? JSON.stringify(bv) : String(bv);
      const cmp = as.localeCompare(bs, undefined, { numeric: true });
      return desc ? -cmp : cmp;
    });
  }, [filteredPlans, sorting]);

  const totalRecords = sortedPlans.length;
  const pageSize = pagination.pageSize;
  const pageCount = Math.max(1, Math.ceil(Math.max(1, totalRecords) / pageSize));
  const safePageIndex = Math.min(pagination.pageIndex, pageCount - 1);

  const pageState: PaginationState = {
    pageIndex: safePageIndex,
    pageSize,
  };

  const paginatedPlans = useMemo(() => {
    const start = safePageIndex * pageSize;
    return sortedPlans.slice(start, start + pageSize);
  }, [sortedPlans, safePageIndex, pageSize]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => saasApi.deletePlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saas-plans'] });
    },
  });

  const handleDelete = async (plan: PlanDto) => {
    if (!globalThis.confirm(`Are you sure you want to delete plan "${plan.name}"?`)) return;
    try {
      await deleteMutation.mutateAsync(String(plan.id));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete plan';
      alert(message);
    }
  };

  const handleAssign = async () => {
    if (!assignPlanId || !tenantId) return;
    try {
      await assignMutation.mutateAsync(assignPlanId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to assign plan';
      alert(message);
    }
  };

  const controllers: DynamicTableControllers = {
    sort: {
      state: sorting,
      onSortingChange: updater => {
        setSorting(prev => (typeof updater === 'function' ? updater(prev) : updater));
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
      },
    },
    pagination: {
      state: pageState,
      pageCount,
      rowCount: totalRecords,
      onPaginationChange: updater => {
        setPagination(prev => (typeof updater === 'function' ? updater(prev) : updater));
      },
    },
  };

  return (
    <div className="space-y-2 p-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">{page('plans', 'title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{page('plans', 'subtitle')}</p>
        </div>
        <PagePrimaryActionButton
          onClick={() => setFormModal({ open: true, mode: 'create', plan: null })}
        >
          {page('plans', 'new')}
        </PagePrimaryActionButton>
      </div>

      {tenantId && plans.length > 0 && (
        <div className="glass-card p-6">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[200px]">
              <label htmlFor="assign-plan" className="block text-sm font-medium text-foreground mb-1">
                Assign plan to this tenant
              </label>
              <select
                id="assign-plan"
                value={assignPlanId}
                onChange={e => setAssignPlanId(e.target.value)}
                className="block w-full px-3 py-2 bg-background border border-border rounded-md focus:ring-2 focus:ring-primary/20"
              >
                <option value="">Select plan</option>
                {plans.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.code})
                  </option>
                ))}
              </select>
            </div>
            <NativeButtonWithIcon
              type="button"
              onClick={handleAssign}
              disabled={!assignPlanId || assignMutation.isPending}
              icon="send"
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50"
            >
              {assignMutation.isPending ? 'Assigning…' : 'Assign'}
            </NativeButtonWithIcon>
          </div>
        </div>
      )}

      {/* Plans table */}
      <div className="glass-card overflow-hidden">
        <div className="flex flex-wrap items-end gap-2 border-b border-border p-3">
          <SearchFilter
            id="plans-name"
            label={col('name')}
            value={search}
            onChange={v => {
              setSearch(v);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            placeholder={page('plans', 'search_placeholder')}
            className="[&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
          <StatusFilter
            id="plans-status"
            value={statusFilter}
            onChange={v => {
              setStatusFilter(v as PlanStatusFilter);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            options={statusOptions}
            emptyOption={allStatusesOption}
            className="[&_button]:h-9 [&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
        </div>
        <PlansTable
          rows={paginatedPlans}
          controllers={controllers}
          rowOffset={pageState.pageIndex * pageState.pageSize}
          onView={p =>
            setFormModal({ open: true, mode: 'view', plan: p })
          }
          onEdit={p =>
            setFormModal({ open: true, mode: 'edit', plan: p })
          }
          onDelete={handleDelete}
          isDeleting={deleteMutation.isPending}
          isLoading={isLoading}
        />
      </div>

      <PlanFormModal
        open={formModal.open}
        onClose={() =>
          setFormModal(prev => ({ ...prev, open: false, plan: null }))
        }
        mode={formModal.mode}
        plan={formModal.plan}
        onSuccess={() =>
          setFormModal(prev => ({ ...prev, open: false, plan: null }))
        }
      />
    </div>
  );
}
